import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { Close } from "@mui/icons-material";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { Variant } from "@mui/material/styles/createTypography";
import AccordionIcon from "assets/icons/AccordionIcon";
import TextFieldControl from "components/form/TextFieldControl";
import VALIDATION from "helpers/validation";
import { ComponentType, MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import { TEXT_FIELD_SX, TYPOGRAPHY_TITLE_SX } from "./constants";
import { getSearchParamsWithIdsParam } from "../helpers";

type WithAccordionProps<T> = T & {
  name: string;
  title: string;
  titleVariant?: Variant;
  isEditable: boolean;
  sx?: SxProps<Theme>;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  entityId?: string;
  idsSearchParamName?: string;
  onRemove: () => void;
};

const withAccordion = <T,>(Component: ComponentType<T>) => {
  // eslint-disable-next-line react/function-component-definition, react/display-name
  return (props: WithAccordionProps<T>) => {
    const {
      name: fieldName,
      title,
      titleVariant = "h6",
      isEditable,
      sx,
      dragHandleProps,
      entityId,
      idsSearchParamName,
      onRemove,
    } = props;
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [entered, setEntered] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]) as {
      scrollIntoViewId?: string;
    } & Record<string, string>;
    const { scrollIntoViewId } = params;
    const searchParamIds = idsSearchParamName ? params[idsSearchParamName] : undefined;
    const searchParamIdsArr = searchParamIds?.split(",") || [];

    const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onRemove();
    };

    const setIdsSearchParam = (id: string, paramName: string) => {
      const resultParams = getSearchParamsWithIdsParam(id, paramName, params);
      setSearchParams(qs.stringify(resultParams), { replace: true });
    };

    const toggleExpanded = () => {
      if (idsSearchParamName && entityId) setIdsSearchParam(entityId, idsSearchParamName);
    };

    useEffect(() => {
      if (idsSearchParamName && entityId) {
        const searchParamIdsEntityIndex = searchParamIdsArr?.findIndex((paramId) => paramId === entityId);

        if (searchParamIdsEntityIndex === -1) {
          if (expanded) setExpanded(false);
        } else if (!expanded) setExpanded(true);
      }
    }, [searchParamIdsArr, idsSearchParamName, entityId]);

    useEffect(() => {
      if (ref && scrollIntoViewId && scrollIntoViewId === entityId && entered) {
        ref.current?.scrollIntoView({ behavior: "smooth" });
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { scrollIntoViewId: _, ...otherParams } = params;
        setSearchParams(qs.stringify(otherParams), { replace: true });
      }
    }, [scrollIntoViewId, ref, entered]);

    return (
      <Accordion
        ref={ref}
        expanded={expanded}
        onChange={toggleExpanded}
        slotProps={{
          transition: {
            onEntered: () => setEntered(true),
            onExited: () => setEntered(false),
          },
        }}
        sx={{
          borderRadius: 2,
          "& .MuiAccordionSummary-content.Mui-expanded": { marginBottom: 0 },
          "& .MuiAccordionSummary-content": { maxWidth: "100%" },
          ...sx,
        }}>
        <div {...dragHandleProps}>
          <AccordionSummary sx={{ "&.Mui-focusVisible": { backgroundColor: "transparent" } }}>
            <Stack
              direction='row'
              spacing={2}
              sx={{ alignItems: "center", width: "100%" }}>
              <Typography
                variant={titleVariant}
                sx={{ fontWeight: 400, ...TYPOGRAPHY_TITLE_SX }}>
                {title}
              </Typography>
              <TextFieldControl
                placeholder='Название'
                name={`${fieldName}.title`}
                disabled={!isEditable}
                rules={{ required: true, ...VALIDATION.competencyMapTitle }}
                stackSx={{ maxWidth: "100%", ...TEXT_FIELD_SX }}
                color='secondary'
                onClick={(e) => e.stopPropagation()}
              />
            </Stack>
            <AccordionActions sx={{ marginLeft: "auto", padding: 0 }}>
              <IconButton
                sx={{
                  color: "base.200",
                  transition: "transform 300ms ease",
                  transform: expanded ? "rotateZ(180deg)" : "none",
                }}>
                <AccordionIcon />
              </IconButton>
              {isEditable && (
                <IconButton
                  onClick={handleRemove}
                  sx={{ color: "base.200" }}>
                  <Close />
                </IconButton>
              )}
            </AccordionActions>
          </AccordionSummary>
        </div>
        <AccordionDetails>
          <Component {...props} />
        </AccordionDetails>
      </Accordion>
    );
  };
};

export default withAccordion;
